/**
 * The INT-slot verdicts, shared by every combinator that reads one.
 * `statedFromFloat` returns `NaN` for a literal that reads but whose stored number
 * the file does not state, `readIntSlot` (in `godot/variant/intSlots.ts`) gives `null` for text the tokenizer cannot read,
 * and NaN fails every comparison, so each combinator gates both.
 */
import { propertyError } from './propertyError.js';
import { readerLimitedInt } from '../../godot/variant/intWidth.js';
import { boolLiteralAsNumber, scalarSlotValue } from '../../godot/variant/variantBool.js';
/**
 * `_to_int<T>` (`variant.h:360-377`): the conversion every int slot's write
 * goes through, and the authority for refusing a literal it cannot carry.
 */
const INT_SLOT_CITE = 'variant.h:360-377';
/**
 * Error for a value that read but cannot be stored; `null` for a usable number.
 * It never names the result: the FLOAT branch (`variant.h:369-370`) is undefined
 * behaviour, so on x86_64 `light_mask = 3e9` stores -2147483648 where `3000000000`
 * stores -1294967296. The message quotes the literal instead.
 */
export function unrepresentableInt(propertyName, key, value, line, errorCodeValue, num, width = 'int32') {
    if (num === null || !Number.isNaN(num))
        return null;
    // Past 2^53 an int64 slot still holds the value exactly, so the limit is this
    // reader's and only warns. It is not silence: the caller reads NaN as
    // "already reported". At narrower widths the engine alters the value, an error.
    const beyondReader = readerLimitedInt(scalarSlotValue(value) ?? NaN, width);
    if (beyondReader) {
        return propertyError(key, line, `Property '${propertyName}' is outside the range this linter reads exactly, got: "${value}". ` +
            `An int64 slot holds it and Godot stores what the file states; no bound on this value is checked here.`, errorCodeValue, 'warning');
    }
    return propertyError(key, line, `Property '${propertyName}' cannot be stored in an integer slot, got: "${value}". ` +
        `The file loads, but the value Godot stores is not the one the file states.`, errorCodeValue, 'error');
}
/**
 * Tag a validator as reading an INT slot: the population `nonFiniteInts.test.ts`
 * sweeps by tag, not by `accepts` prose, which `layerBitmask` overwrites. `width`
 * is the slot's C++ type, since `4294967296` is unstorable in int32 and exact in int64.
 */
export function markIntSlot(validator, width = 'int32') {
    // Never format-only: `inf` reads (`variant_parser.cpp:701-707`) and is altered
    // on the write.
    delete validator.formatOnly;
    // Not `grounding`, which says where a bound's authority comes from:
    // `validatorClassification` accepts `intSlot` only for a validator with no bounds.
    validator.intSlot = { cite: INT_SLOT_CITE, width };
    return validator;
}
/**
 * A cross-family spelling warning: the value is stored, but not as written, so a
 * re-save rewrites the line. A warning, as {@link storedNotWritten} is:
 * `can_convert_strict` accepts the whole BOOL/INT/FLOAT family
 * (`variant.cpp:550-583`). The int half is folded into {@link storedNotWritten}.
 */
export function convertedSpelling(propertyName, key, value, line, code, storedText, converted) {
    if (!converted)
        return null;
    return propertyError(key, line, `Property '${propertyName}' is written "${value.trim()}", which this slot converts: ` +
        `Godot stores ${storedText} and writes it back that way.`, code, 'warning');
}
/**
 * The warning for a literal an INT slot stores as something else, or `null`.
 * `_to_int` (`variant.h:361-377`) maps a BOOL to 1/0 and truncates a FLOAT, so
 * `cast_shadow = true` stores 1 and `hframes = 5.5` stores 5. Every int reader
 * applies it last, after every bound, since an out-of-range error outranks it.
 */
export function storedNotWritten(propertyName, key, value, line, errorCodeValue, read) {
    // The caller's read, so the literal is parsed once.
    const { asFloat, stored } = read;
    if (stored === null || Number.isNaN(stored))
        return null;
    // The BOOL arm first: `true` reads as a whole 1, so the fractional test below
    // returns null past it and the conversion would go unreported.
    const converted = convertedSpelling(propertyName, key, value, line, errorCodeValue, String(stored), boolLiteralAsNumber(value) !== undefined);
    if (converted)
        return converted;
    if (asFloat === null || Number.isInteger(asFloat))
        return null;
    // A warning, not an error: the conversion (`variant.h:369-370`) happens before
    // the setter, which only sees the 5. A `_VALUE` code, never `_FORMAT`: the
    // tokenizer reads `5.5` (`variant_parser.cpp:443-448` types it FLOAT).
    return propertyError(key, line, `Property '${propertyName}' is an integer slot, so Godot drops the fractional part of "${value}" and stores ${stored}.`, errorCodeValue, 'warning');
}
/**
 * {@link storedNotWritten} for the components of an integer composite:
 * `_parse_construct<int32_t>` (`variant_parser.cpp:577-592`) narrows any number
 * token, so `Vector2i(1.5, 2)` stores `(1, 2)`. Its callers' widened grammar
 * admits `inf`/`nan`, so it guards finiteness itself.
 */
export function truncatedComponent(propertyName, key, line, components, errorCodeValue) {
    for (const text of components) {
        if (text === undefined)
            continue;
        const asFloat = scalarSlotValue(text);
        // `Number.isFinite` first: `Number.isInteger` is false for Infinity and NaN,
        // and ADR-0032 forbids naming an undefined conversion's result. A non-finite
        // component is the unstorable refusal, reported by the caller's own arm.
        if (asFloat === null || !Number.isFinite(asFloat) || Number.isInteger(asFloat))
            continue;
        return propertyError(key, line, `Property '${propertyName}' has integer components, so Godot drops the fractional part of "${text.trim()}" and stores ${Math.trunc(asFloat)}.`, errorCodeValue, 'warning');
    }
    return null;
}
//# sourceMappingURL=intSlot.js.map